import { useQuery } from '@apollo/client/react';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import {
  faArrowRightArrowLeft,
  faCircleCheck,
  faCircleHalfStroke,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cloneDeep from 'lodash/cloneDeep';
import flattenDeep from 'lodash/flattenDeep';
import get from 'lodash/get';
import merge from 'lodash/merge';
import React, { useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import {
  ExerciseDetailsFragment,
  ExerciseUnit,
  ExerciseUnitsDocument,
  TrainingPlanWeekDetailsFragment,
  TrainingPlanWorkoutDetailsFragment,
} from '../../../graphql/types';
import { TrackingEventData } from '../../../hooks/useTracking';
import theme, {
  AQUAMARINE,
  BORDER_GRAY,
  GREEN500,
  WHITE,
} from '../../../theme';
import { shortMonthDay } from '../../../utils/date';
import { mergeKeepShape } from '../../../utils/mergeKeepShape';
import { EventCategories, EventLabels } from '../../../utils/trackingEvents';
import { MovementDetailModal } from '../../MovementLibrary/MovementDetailModal';
import Button, { ButtonSize, ButtonVariant } from '../../reusable/Button';
import { IconButton } from '../../reusable/Button/IconButton';
import Errors from '../../reusable/ErrorMessage';
import Input from '../../reusable/Input';
import { Select } from '../../reusable/Select';
import Text, { TextColor, TextKind } from '../../reusable/Text';
import { TextLineClamp } from '../../reusable/TextLineClamp';
import DurationInput from './DurationInput';
import { ExerciseAutocomplete } from './ExerciseAutocomplete';
import { formatHMS } from './util';

const FEATURE_HEADER_HEIGHT = 49; // measured
const WORKOUT_HEADER_HEIGHT = 30;
const WORKOUT_HEADER_BOTTOM_SPACING = 22;

/** Horizontal padding for workout table header & body cells */
const TABLE_PAD_X = 16;
/** Vertical rhythm: top padding per row; same value for bottom on last row / before block divider */
const TABLE_PAD_Y = 8;

const TABLE_STRIPE_EVEN = theme.palette.grey[50];
const TABLE_STRIPE_ODD = theme.palette.grey[100];
/** Same column parity as body stripes, two shades darker for thead */
const TABLE_HEADER_STRIPE_EVEN = theme.palette.grey[200];
const TABLE_HEADER_STRIPE_ODD = theme.palette.grey[300];

const StyledWorkoutTable = styled.table`
  width: 100%;
  border-spacing: 0;
  border-collapse: separate;
`;

const StyledWorkoutHeader = styled.div`
  position: sticky;
  z-index: 1;
  top: ${FEATURE_HEADER_HEIGHT}px;
  padding-bottom: ${WORKOUT_HEADER_BOTTOM_SPACING}px;
  padding-top: ${WORKOUT_HEADER_BOTTOM_SPACING}px;
  background: ${WHITE};
  display: flex;
  align-items: center;
  gap: 16px;
  height: ${WORKOUT_HEADER_HEIGHT}px;
`;

const StyledWorkoutHeaderActionContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
`;

const StyledWorkoutTableHead = styled.thead`
  position: sticky;
  top: ${FEATURE_HEADER_HEIGHT + WORKOUT_HEADER_HEIGHT + WORKOUT_HEADER_BOTTOM_SPACING * 2}px;
  z-index: 1;

  th {
    padding: ${TABLE_PAD_X}px;
    text-align: left;
    border-bottom: 1px solid ${BORDER_GRAY};
    outline: none;
  }
  th:nth-child(even) {
    background-color: ${TABLE_HEADER_STRIPE_EVEN};
  }
  th:nth-child(odd) {
    background-color: ${TABLE_HEADER_STRIPE_ODD};
  }
`;

const StyledWorkoutTableBody = styled.tbody`
  && tr {
    border: 0 !important;
  }

  && td {
    padding: ${TABLE_PAD_Y}px ${TABLE_PAD_X}px 0;
    vertical-align: top;
    border: 0 !important;
    box-shadow: none !important;
    outline: none;
  }
  && tr:last-child td,
  && tr:has(+ tr[data-block-divider]) td {
    padding-bottom: ${TABLE_PAD_Y}px;
  }

  td:nth-child(even) {
    background-color: ${TABLE_STRIPE_EVEN};
  }
  td:nth-child(odd) {
    background-color: ${TABLE_STRIPE_ODD};
  }
`;

const StyledDisplayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const MovementActivityCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

interface HiddenInputProps {
  display: React.ReactElement;
  input: React.ReactElement;
  isEditable: boolean;
  fieldName: string; // Field name for error display
}

const ConditionalInput: React.FC<HiddenInputProps> = ({
  display,
  input,
  isEditable,
  fieldName,
}) => {
  if (!isEditable) {
    return (
      <StyledDisplayWrapper>
        <TextLineClamp $lines={1}>{display}</TextLineClamp>
      </StyledDisplayWrapper>
    );
  }
  return (
    <StyledDisplayWrapper>
      {input}
      {fieldName && <Errors name={fieldName} errorTextKind={TextKind.BodyS} />}
    </StyledDisplayWrapper>
  );
};

const getWorkoutState = ({
  workout,
  canEdit,
}: {
  workout: TrainingPlanWorkoutDetailsFragment;
  canEdit: boolean;
}) => {
  const trainingPlanExercisesInWeek = workout.blocks.edges.map(
    ({ node: block }) =>
      block.sets.edges.map(({ node: set }) =>
        set.trainingPlanExercises.edges.map(
          ({ node: trainingPlanExercise }) => trainingPlanExercise,
        ),
      ),
  );
  const flattenedTrainingPlanExercises = flattenDeep(
    trainingPlanExercisesInWeek,
  ).filter((e) => e.completedAt);

  const workoutHasStarted = flattenedTrainingPlanExercises.some(
    (e) => e.completedAt,
  );
  const workoutCompleted = workout.completedAt;
  const canEditWorkout = canEdit && !workoutHasStarted && !workoutCompleted;

  return { workoutHasStarted, workoutCompleted, canEditWorkout };
};

const getWorkoutPath = (weekIndex: number, workoutIndex: number) =>
  `trainingPlan.weeks.edges.${weekIndex}.node.workouts.edges.${workoutIndex}.node` as const;

const getBlockPath = (workoutPath: string, blockOrder: number) =>
  `${workoutPath}.blocks.edges.${blockOrder}.node` as const;

const getTrainingPlanExercisePath = (
  blockPath: string,
  exerciseOrder: number,
) =>
  `${blockPath}.sets.edges.0.node.trainingPlanExercises.edges.${exerciseOrder}.node` as const;

const workoutSetOptions = Array.from({ length: 10 }, (_, i) => i + 1).map(
  (num) => ({
    label: `${num} set${num > 1 ? 's' : ''}`,
    value: num,
  }),
);

const MAX_UNITS = new Set<string>([ExerciseUnit.RepsMax, ExerciseUnit.TimeMax]);
export const isMaxUnit = (unit?: string): boolean =>
  Boolean(unit && MAX_UNITS.has(unit));

const TIME_UNITS = new Set<string>([
  ExerciseUnit.Seconds,
  ExerciseUnit.SecondsEach,
]);
export const isTimeUnit = (unit?: string): boolean =>
  Boolean(unit && TIME_UNITS.has(unit));

export const validateExerciseCountMin = (
  value: number,
  unitValue?: string,
): true | string => {
  if (isMaxUnit(unitValue)) {
    return true;
  }
  return value >= 1 || 'Amount must be positive';
};

interface TrainingPlanWorkoutProps {
  workout: TrainingPlanWorkoutDetailsFragment;
  weekOrder: number;
  canEdit?: boolean;
  trackingData?: TrackingEventData;
}

export const TrainingPlanWorkout: React.FC<TrainingPlanWorkoutProps> = ({
  workout,
  weekOrder,
  canEdit = false,
  trackingData,
}) => {
  const {
    watch,
    setValue,
    getValues,
    resetField,
    formState,
    trigger,
    setFocus,
  } = useFormContext();
  const { data: exerciseUnitsData } = useQuery(ExerciseUnitsDocument);
  const exerciseUnits = exerciseUnitsData?.exerciseUnits || [];
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [selectedExerciseId, setSelectedExerciseId] = useState<string | null>(
    null,
  );
  const [replaceFieldPaths, setReplaceFieldPaths] = useState<{
    exercisePath: string;
    unitPath: string;
  } | null>(null);
  const copySuccessTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Memoize workout state calculations
  const { workoutCompleted, workoutHasStarted, canEditWorkout } = useMemo(
    () => getWorkoutState({ workout, canEdit }),
    [workout, canEdit],
  );

  const currentWorkoutFormPath = getWorkoutPath(weekOrder, workout.order);
  const dirtyWorkout = get(formState.dirtyFields, currentWorkoutFormPath) || {};
  const workoutErrors = get(formState.errors, currentWorkoutFormPath) || {};
  const workoutValue = getValues(currentWorkoutFormPath);
  const nextWorkoutValue = getValues(
    getWorkoutPath(weekOrder + 1, workout.order),
  );
  const dirtyWorkoutValues = useMemo(
    () => mergeKeepShape(cloneDeep(dirtyWorkout), cloneDeep(workoutValue)),
    [dirtyWorkout, workoutValue],
  );

  const onCopyToFutureWeeksClick = (workoutIndex: number) => {
    const dirtyWorkoutValues = mergeKeepShape(
      cloneDeep(dirtyWorkout),
      cloneDeep(workoutValue),
    );
    const allWeeks = getValues('trainingPlan.weeks.edges') as {
      node: TrainingPlanWeekDetailsFragment;
    }[];
    const currentWorkoutData = getValues(currentWorkoutFormPath);
    resetField(currentWorkoutFormPath, {
      defaultValue: merge(
        cloneDeep(dirtyWorkoutValues),
        cloneDeep(currentWorkoutData),
      ),
      keepError: true,
    });

    // Apply the current exercise data to all future weeks
    allWeeks.forEach((_, futureWeekIndex: number) => {
      const futureWorkoutData = getValues(
        getWorkoutPath(futureWeekIndex, workoutIndex),
      );

      const merged = merge(
        cloneDeep(futureWorkoutData),
        cloneDeep(dirtyWorkoutValues),
      );

      if (merged && futureWeekIndex > weekOrder) {
        // Copy the exercise data from the current week to future weeks
        setValue(getWorkoutPath(futureWeekIndex, workoutIndex), merged, {
          shouldDirty: false,
        });
      }
    });

    copySuccessTimeoutRef.current &&
      clearTimeout(copySuccessTimeoutRef.current);
    setShowCopySuccess(true);
    copySuccessTimeoutRef.current = setTimeout(() => {
      setShowCopySuccess(false);
    }, 3500);
  };

  const hasDirtyFields = Object.keys(dirtyWorkoutValues || {}).length > 0;
  const hasErrors = Object.keys(workoutErrors || {}).length > 0;
  const hasNextWorkout = Boolean(nextWorkoutValue);

  const showCopyToFutureWeeksButton =
    hasDirtyFields && !hasErrors && hasNextWorkout;

  const showCopySuccessIndicator =
    showCopySuccess && !showCopyToFutureWeeksButton;

  // Memoize exercise units options to avoid recreating on each render
  const exerciseUnitOptions = useMemo(
    () =>
      exerciseUnits.map((exerciseUnit) => ({
        name: exerciseUnit.name,
        label: exerciseUnit.name,
        value: exerciseUnit.value,
      })),
    [exerciseUnits],
  );

  const exercisesByBlock = flattenDeep(
    workout.blocks.edges.map(({ node: block }) =>
      block.sets.edges.slice(0, 1).map(({ node: set }) =>
        set.trainingPlanExercises.edges.map(
          ({ node: trainingPlanExercise }) => ({
            ...trainingPlanExercise,
            block,
          }),
        ),
      ),
    ),
  );

  return (
    <React.Fragment key={workout.id}>
      <StyledWorkoutHeader>
        <Text kind={TextKind.TitleM}>Day {workout.order + 1} Workout </Text>

        {workoutCompleted && (
          <Text
            kind={TextKind.BodyS}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <FontAwesomeIcon
              icon={faCircleCheck}
              fontSize={16}
              color={GREEN500}
            />{' '}
            Completed {shortMonthDay(workoutCompleted)}
          </Text>
        )}
        {!workoutCompleted && workoutHasStarted && (
          <Text
            kind={TextKind.BodyS}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <FontAwesomeIcon
              icon={faCircleHalfStroke}
              fontSize={16}
              color={GREEN500}
            />
            In Progress
          </Text>
        )}
        <StyledWorkoutHeaderActionContainer>
          {showCopyToFutureWeeksButton && (
            <Button
              trackingData={{
                ...trackingData,
                event_label: EventLabels.CopyToFutureWeeks,
              }}
              onClick={() => onCopyToFutureWeeksClick(workout.order)}
            >
              Copy edits to future weeks
            </Button>
          )}
          {showCopySuccessIndicator && (
            <>
              <FontAwesomeIcon
                icon={faCheckCircle}
                color={AQUAMARINE}
                fontSize={12}
              />
              <Text testId="copyToFutureWeeks-Success" kind={TextKind.BodyS}>
                Edits copied to future weeks!
              </Text>
            </>
          )}
        </StyledWorkoutHeaderActionContainer>
      </StyledWorkoutHeader>
      <StyledWorkoutTable style={{ opacity: canEditWorkout ? 1 : 0.4 }}>
        <colgroup>
          {/* block index */}
          <col style={{ width: '2%' }} />
          {/* block */}
          <col width="15%" />
          {/* sets */}
          <col width="10%" />
          {/* movements */}
          <col width="38%" />
          {/* amount */}
          <col width="10%" />
          {/* unit */}
          <col width="25%" />
        </colgroup>
        <StyledWorkoutTableHead>
          <tr>
            <th />
            <th>
              <Text kind={TextKind.LabelS} color={TextColor.Secondary}>
                Block
              </Text>
            </th>
            <th>
              <Text kind={TextKind.LabelS} color={TextColor.Secondary}>
                Sets
              </Text>
            </th>
            <th>
              <Text kind={TextKind.LabelS} color={TextColor.Secondary}>
                Movements & activities
              </Text>
            </th>
            <th>
              <Text kind={TextKind.LabelS} color={TextColor.Secondary}>
                Amount
              </Text>
            </th>
            <th>
              <Text kind={TextKind.LabelS} color={TextColor.Secondary}>
                Unit
              </Text>
            </th>
          </tr>
        </StyledWorkoutTableHead>
        <StyledWorkoutTableBody>
          {exercisesByBlock.map((exercise) => {
            const block = exercise.block;
            const blockPath = getBlockPath(
              currentWorkoutFormPath,
              exercise.block.order,
            );
            const trainingPlanExercisePath = getTrainingPlanExercisePath(
              blockPath,
              exercise.order,
            );
            const exercisePath = `${trainingPlanExercisePath}.exercise`;
            const exerciseUnitPath = `${trainingPlanExercisePath}.unit`;
            const exerciseCountPath = `${trainingPlanExercisePath}.count`;
            const setsCountPath = `${blockPath}.sets.count`;
            const currentUnit = watch(exerciseUnitPath);
            const currentExerciseId = watch(`${exercisePath}.id`) as
              | string
              | undefined;

            const isFirstExerciseInBlock = exercise.order === 0;
            const showBlockSeparator =
              isFirstExerciseInBlock && exercise.block.order > 0;

            return (
              <React.Fragment key={`block-${exercise.id}`}>
                {showBlockSeparator && (
                  <tr key={`separator-${exercise.id}`} data-block-divider="">
                    <td
                      colSpan={100}
                      style={{
                        height: '1px',
                        backgroundColor: BORDER_GRAY,
                        padding: 0,
                        borderTop: 'none',
                        borderBottom: 'none',
                      }}
                    />
                  </tr>
                )}
                <tr>
                  <td>
                    {isFirstExerciseInBlock && (
                      <Text
                        kind={TextKind.ButtonM}
                        color={TextColor.Secondary}
                        textTransform="none"
                        title={`Block ${block.order + 1}`}
                        testId={`block-index-${block.id}`}
                      >
                        {block.order + 1}
                      </Text>
                    )}
                  </td>
                  <td>
                    {isFirstExerciseInBlock && (
                      <Text kind={TextKind.ButtonM}>
                        {block.trainingComponent.name}
                      </Text>
                    )}
                  </td>
                  <td>
                    {isFirstExerciseInBlock && (
                      <Controller
                        name={setsCountPath}
                        render={({ field }) => (
                          <ConditionalInput
                            isEditable={canEditWorkout}
                            fieldName={setsCountPath}
                            display={
                              <Text
                                kind={TextKind.BodyM}
                                testId={`blockSetCountLabel-${block.id}`}
                              >
                                {field.value} set
                                {field.value > 1 ? 's' : ''}
                              </Text>
                            }
                            input={
                              <Select
                                onChange={({ value }) => {
                                  field.onChange(value, {
                                    shouldDirty: true,
                                  });
                                }}
                                name={`blockSetCount-${block.id}`}
                                value={{
                                  label: `${field.value} set${field.value > 1 ? 's' : ''}`,
                                  value: block.sets.count,
                                }}
                                trackingData={{
                                  event_label: EventLabels.TrainingPlanSets,
                                  event_category: EventCategories.TrainingPlans,
                                  block_id: block.id,
                                }}
                                fullWidth
                                options={workoutSetOptions}
                              />
                            }
                          />
                        )}
                      />
                    )}
                  </td>
                  <td>
                    <MovementActivityCell>
                      <Controller
                        key={exercise.id}
                        name={exercisePath}
                        render={({ field }) => (
                          <ConditionalInput
                            key={exercise.id}
                            fieldName={exercisePath}
                            isEditable={canEditWorkout}
                            display={
                              <Text
                                testId={`exerciseName-${exercise.id}`}
                                kind={TextKind.BodyM}
                              >
                                {field.value?.displayName}
                              </Text>
                            }
                            input={
                              <ExerciseAutocomplete
                                onChange={(
                                  selectedExercise: ExerciseDetailsFragment | null,
                                ) => {
                                  if (selectedExercise) {
                                    setValue(exercisePath, selectedExercise, {
                                      shouldDirty: true,
                                    });
                                    if (selectedExercise.defaultUnit) {
                                      setValue(
                                        exerciseUnitPath,
                                        {
                                          value:
                                            selectedExercise.defaultUnit?.value,
                                          name: selectedExercise.defaultUnit
                                            ?.name,
                                        },
                                        { shouldDirty: true },
                                      );
                                      if (
                                        selectedExercise.defaultUnit.value !==
                                        field.value?.defaultUnit?.value
                                      ) {
                                        setValue(exerciseCountPath, '', {
                                          shouldDirty: true,
                                        });
                                      }
                                    } else {
                                      setValue(
                                        exerciseUnitPath,
                                        { name: '', value: null },
                                        {
                                          shouldDirty: true,
                                          shouldValidate: true,
                                        },
                                      );
                                    }

                                    setTimeout(() =>
                                      setFocus(exerciseCountPath),
                                    );
                                  }
                                }}
                                value={field.value || exercise}
                                trackingData={trackingData}
                              />
                            }
                          />
                        )}
                      />
                      <IconButton
                        testId={`exercise-detail-${exercise.id}`}
                        ariaLabel="View or replace movement"
                        variant={ButtonVariant.Transparent}
                        size={ButtonSize.Small}
                        disabled={!currentExerciseId}
                        onClick={() => {
                          setSelectedExerciseId(currentExerciseId ?? null);
                          setReplaceFieldPaths({
                            exercisePath,
                            unitPath: exerciseUnitPath,
                          });
                        }}
                        icon={
                          <FontAwesomeIcon
                            icon={faArrowRightArrowLeft}
                            fontSize={16}
                          />
                        }
                      />
                    </MovementActivityCell>
                  </td>
                  <td>
                    <Controller
                      key={exercise?.id}
                      name={exerciseCountPath}
                      rules={{
                        required: 'Amount is required',
                        validate: {
                          min: (value) =>
                            validateExerciseCountMin(
                              value,
                              getValues(exerciseUnitPath)?.value,
                            ),
                          max: (value) => {
                            const unit = getValues(exerciseUnitPath);
                            if (
                              isMaxUnit(unit?.value) ||
                              isTimeUnit(unit?.value)
                            ) {
                              return true;
                            }
                            return value <= 999 || 'Amount must be 999 or less';
                          },
                        },
                      }}
                      render={({ field }) =>
                        isMaxUnit(currentUnit?.value) ? (
                          <div
                            style={{
                              display: 'flex',
                              minHeight: '40px',
                              alignItems: 'center',
                            }}
                          >
                            <Text
                              testId={`exerciseCount-${exercise?.id}`}
                              kind={TextKind.BodyM}
                            >
                              max
                            </Text>
                          </div>
                        ) : (
                          <ConditionalInput
                            isEditable={canEditWorkout}
                            fieldName={exerciseCountPath}
                            display={
                              <Text
                                testId={`exerciseCount-${exercise?.id}`}
                                kind={TextKind.BodyM}
                              >
                                {isTimeUnit(currentUnit?.value)
                                  ? formatHMS(field.value)
                                  : field.value}
                              </Text>
                            }
                            input={
                              isTimeUnit(currentUnit?.value) ? (
                                <DurationInput
                                  id={`exercise-amount-${exercise?.id}`}
                                  totalSeconds={field.value}
                                  onChange={field.onChange}
                                  onBlur={field.onBlur}
                                />
                              ) : (
                                <Input
                                  id={`exercise-amount-${exercise?.id}`}
                                  type="number"
                                  renderError={false}
                                  trackingData={{
                                    event_label:
                                      EventLabels.TrainingPlanExerciseAmount,
                                    event_category:
                                      EventCategories.TrainingPlans,
                                    exercise_id: exercise?.id,
                                  }}
                                  {...field}
                                />
                              )
                            }
                          />
                        )
                      }
                    />
                  </td>
                  <td>
                    <Controller
                      key={exercise.id}
                      name={exerciseUnitPath}
                      rules={{
                        validate: (value) =>
                          Boolean(value.value) || 'Unit is required',
                      }}
                      render={({ field }) => (
                        <ConditionalInput
                          fieldName={exerciseUnitPath}
                          isEditable={canEditWorkout}
                          display={
                            <Text
                              testId={`exerciseUnit-${exercise?.id}`}
                              kind={TextKind.BodyM}
                            >
                              {field.value.name}
                            </Text>
                          }
                          input={
                            <Select
                              {...field}
                              testId={`exerciseUnitSelect-${exercise.id}`}
                              onChange={async (value) => {
                                field.onChange(value);
                                if (
                                  typeof value.value === 'string' &&
                                  isMaxUnit(value.value)
                                ) {
                                  setValue(exerciseCountPath, 0, {
                                    shouldValidate: false,
                                    shouldDirty: true,
                                  });
                                }
                                // Manually trigger validation due to amount and unit fields being intertwined
                                await trigger(exerciseCountPath);
                              }}
                              trackingData={{
                                event_label:
                                  EventLabels.TrainingPlanExerciseUnit,
                                event_category: EventCategories.TrainingPlans,
                                exercise_id: exercise.id,
                              }}
                              fullWidth
                              value={{
                                ...field.value,
                                label: field.value.name,
                              }}
                              options={exerciseUnitOptions}
                            />
                          }
                        />
                      )}
                    />
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </StyledWorkoutTableBody>
      </StyledWorkoutTable>
      <MovementDetailModal
        exerciseId={selectedExerciseId}
        onClose={() => {
          setSelectedExerciseId(null);
          setReplaceFieldPaths(null);
        }}
        onOpenExercise={(id) => setSelectedExerciseId(id)}
        onReplace={
          replaceFieldPaths
            ? (exercise) => {
                setValue(replaceFieldPaths.exercisePath, exercise, {
                  shouldDirty: true,
                });
                if (exercise.defaultUnit) {
                  setValue(replaceFieldPaths.unitPath, exercise.defaultUnit, {
                    shouldDirty: true,
                  });
                }
              }
            : undefined
        }
      />
    </React.Fragment>
  );
};
